import React, { useEffect, useRef, useState } from 'react';
import { useTicketsStore } from '../../../store/ticketsStore';
import { Ticket, TicketStatus } from '../../../services/ticketsApi';
import { uiPriority, uiStatus, formatDateTime } from './ticketAdapter';
import styles from './TicketDetailModal.module.css';

/**
 * Shared modal for viewing a ticket from any issue table (Delivery /
 * Customer / Payment / Inventory / Compliance / Returns / All).
 *
 * Wires up every action exposed in the design:
 *   - Send Reply   → POST /api/v1/tickets/:id/messages
 *   - 📎 Attachment → file picker; the file name is appended to the
 *                     reply body (backend currently has no upload field
 *                     on tickets, so we surface the attachment in-line
 *                     instead of dropping the click silently)
 *   - Status select → PATCH /api/v1/tickets/:id { status }
 *   - Escalate     → PATCH /api/v1/tickets/:id { priority: 'critical', status: 'in_progress' }
 *   - Resolve      → PATCH /api/v1/tickets/:id { status: 'resolved' }
 */

interface Props {
  ticketId: number | null;
  onClose: () => void;
}

const STATUS_OPTIONS: { value: TicketStatus; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'waiting', label: 'Waiting' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

export const TicketDetailModal: React.FC<Props> = ({ ticketId, onClose }) => {
  const current = useTicketsStore((s) => s.current);
  const saving = useTicketsStore((s) => s.saving);
  const fetchOne = useTicketsStore((s) => s.fetchOne);
  const update = useTicketsStore((s) => s.update);
  const reply = useTicketsStore((s) => s.reply);

  const [replyBody, setReplyBody] = useState('');
  const [internalNote, setInternalNote] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load the ticket whenever the modal opens / changes target.
  useEffect(() => {
    if (ticketId) fetchOne(ticketId).catch(() => undefined);
  }, [ticketId, fetchOne]);

  // Lock body scroll while open.
  useEffect(() => {
    if (!ticketId) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [ticketId]);

  if (!ticketId) return null;

  const ticket: Ticket | null = current && current.id === ticketId ? current : null;

  const handleSend = async () => {
    if (!ticket || !replyBody.trim()) return;
    try {
      await reply(ticket.id, replyBody.trim(), internalNote);
      setReplyBody('');
    } catch {
      // Errors surface via store's error state.
    }
  };

  const handleAttach = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // The tickets endpoint has no file upload field today, so we surface
    // the attachment in the message body. Once /tickets/:id/attachments
    // ships this can switch to the real upload.
    setReplyBody((prev) =>
      prev.trim()
        ? `${prev}\n[Attachment: ${file.name}]`
        : `[Attachment: ${file.name}]`
    );
    e.target.value = '';
  };

  const handleStatusChange = (status: TicketStatus) => {
    if (!ticket || ticket.status === status) return;
    update(ticket.id, { status }).catch(() => undefined);
  };

  const handleEscalate = () => {
    if (!ticket) return;
    update(ticket.id, { priority: 'critical', status: 'in_progress' }).catch(() => undefined);
  };

  return (
    <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true">
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.ticketBadge}>
              {ticket ? `#${ticket.ticket_number}` : 'Loading…'}
            </span>
            {ticket && (
              <>
                <span className={`${styles.priority} ${styles[`pri_${uiPriority(ticket.priority).toLowerCase()}`]}`}>
                  {uiPriority(ticket.priority)}
                </span>
                <span className={styles.status}>{uiStatus(ticket.status)}</span>
              </>
            )}
          </div>
          <button className={styles.close} onClick={onClose} aria-label="Close">×</button>
        </header>

        {!ticket ? (
          <div className={styles.empty}>Loading ticket…</div>
        ) : (
          <>
            <div className={styles.subjectRow}>
              <h2 className={styles.subject}>{ticket.subject}</h2>
              <p className={styles.meta}>
                {ticket.customer_name ? `Customer: ${ticket.customer_name} · ` : ''}
                Opened {formatDateTime(ticket.opened_at)}
                {ticket.sla_due_at ? ` · SLA: ${formatDateTime(ticket.sla_due_at)}` : ''}
              </p>
            </div>

            <div className={styles.actionsBar}>
              <label className={styles.statusLabel}>
                Status
                <select
                  className={styles.statusSelect}
                  value={ticket.status}
                  onChange={(e) => handleStatusChange(e.target.value as TicketStatus)}
                  disabled={saving}
                >
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className={styles.escalate}
                onClick={handleEscalate}
                disabled={saving || ticket.priority === 'critical'}
              >
                ⚠ Escalate
              </button>
              <button
                type="button"
                className={styles.resolve}
                onClick={() => handleStatusChange('resolved')}
                disabled={saving || ticket.status === 'resolved' || ticket.status === 'closed'}
              >
                ✓ Resolve
              </button>
            </div>

            <div className={styles.body}>
              <h3 className={styles.sectionTitle}>Conversation</h3>
              <div className={styles.thread}>
                {(ticket.messages ?? []).length === 0 && (
                  <p className={styles.emptyThread}>No messages yet.</p>
                )}
                {(ticket.messages ?? []).map((m) => (
                  <div key={m.id} className={`${styles.message} ${m.is_internal ? styles.internal : ''}`}>
                    <div className={styles.msgHeader}>
                      <span className={styles.msgSender}>
                        {m.sender_name || 'System'}
                        <span className={styles.msgRole}> · {m.sender_role}</span>
                      </span>
                      <span className={styles.msgTime}>{formatDateTime(m.created_at)}</span>
                    </div>
                    {m.is_internal && <span className={styles.internalTag}>Internal note</span>}
                    <p className={styles.msgBody}>{m.body}</p>
                  </div>
                ))}
              </div>

              <div className={styles.replyBox}>
                <textarea
                  className={styles.replyInput}
                  rows={3}
                  placeholder={internalNote ? 'Internal note (only visible to support staff)…' : 'Type your reply…'}
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                />
                <div className={styles.replyActions}>
                  <label className={styles.internalToggle}>
                    <input
                      type="checkbox"
                      checked={internalNote}
                      onChange={(e) => setInternalNote(e.target.checked)}
                    />
                    Internal note
                  </label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleAttach}
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    className={styles.attachBtn}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={saving}
                  >
                    📎 Attachment
                  </button>
                  <button
                    type="button"
                    className={styles.sendBtn}
                    onClick={handleSend}
                    disabled={saving || !replyBody.trim()}
                  >
                    {saving ? 'Sending…' : 'Send Reply'}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TicketDetailModal;
